export function unwrapApiPayload(response) {
  if (!response) return null;

  let payload = response;
  for (let i = 0; i < 3; i += 1) {
    if (payload?.success && payload.data != null) {
      payload = payload.data;
      continue;
    }
    if (payload?.data != null && !Array.isArray(payload)) {
      const inner = payload.data;
      if (Array.isArray(inner) || typeof inner === "object") {
        payload = inner;
        continue;
      }
    }
    break;
  }

  return payload;
}

const X_AXIS_KEYS = new Set([
  "month",
  "name",
  "label",
  "date",
  "day",
  "period",
  "year",
  "_id",
  "id",
]);

const VALUE_KEYS = [
  "users",
  "user",
  "userCount",
  "totalUsers",
  "activeUsers",
  "activeUserCount",
  "newUsers",
  "newUserCount",
  "addedUsers",
  "noOfUsers",
  "numberOfUsers",
  "currentMonthAdded",
  "lastMonthAdded",
  "count",
  "growth",
  "userGrowth",
  "value",
  "total",
  "added",
];

const LINE_NAMES = {
  users: "Users",
  userCount: "Users",
  totalUsers: "Total Users",
  activeUsers: "Active Users",
  activeUserCount: "Active Users",
  newUsers: "New Users",
  newUserCount: "New Users",
  addedUsers: "Added Users",
  currentMonthAdded: "New Users",
  lastMonthAdded: "Users",
  count: "Users",
  growth: "Growth",
  userGrowth: "User Growth",
  value: "Users",
  total: "Total Users",
  added: "Added Users",
};

const LINE_COLORS = ["#5C308D", "#00A63E", "#2563EB", "#D97706"];

function isNumericValue(value) {
  if (value == null || value === "") return false;
  if (typeof value === "number" && !Number.isNaN(value)) return true;
  if (typeof value === "string" && !Number.isNaN(Number(value))) return true;
  return false;
}

function flattenPoint(point) {
  if (!point || typeof point !== "object") return point;

  const flat = { ...point };

  for (const [key, value] of Object.entries(point)) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        if (flat[nestedKey] == null) {
          flat[nestedKey] = nestedValue;
        }
      }
      delete flat[key];
    }
  }

  return flat;
}

function buildPointsFromParallelArrays(payload) {
  const labels = payload.months ?? payload.labels ?? payload.categories;
  if (!Array.isArray(labels) || !labels.length) return null;

  const valueKeys = Object.keys(payload).filter(
    (key) =>
      !["months", "labels", "categories", "title", "subtitle", "xKey", "lines"].includes(
        key
      ) &&
      Array.isArray(payload[key]) &&
      payload[key].length === labels.length
  );

  if (!valueKeys.length) return null;

  return labels.map((label, index) => {
    const point =
      typeof label === "object" && label !== null
        ? { ...label }
        : { month: label };

    for (const key of valueKeys) {
      point[key] = payload[key][index];
    }

    return point;
  });
}

function resolvePoints(payload) {
  if (!payload) return null;

  const parallelPoints = buildPointsFromParallelArrays(payload);
  if (parallelPoints?.length) return parallelPoints;

  const arrayCandidates = [
    payload,
    payload.data,
    payload.points,
    payload.growth,
    payload.activity,
    payload.months,
    payload.userActivityGrowth,
    payload.chartData,
    payload.series,
  ];

  for (const candidate of arrayCandidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate.map(flattenPoint);
    }
  }

  return null;
}

function resolveXKey(points, payload) {
  if (payload?.xKey) return payload.xKey;
  if (!points?.length) return "month";

  const sample = points[0];
  if (sample.month != null) return "month";
  if (sample.name != null) return "name";
  if (sample.label != null) return "label";
  if (sample.date != null) return "date";
  if (sample._id != null && typeof sample._id === "string") return "_id";
  return "month";
}

function detectValueKeys(points) {
  if (!points?.length) return [];

  const fromKnown = VALUE_KEYS.filter((key) =>
    points.some((point) => isNumericValue(point[key]))
  );
  if (fromKnown.length) return fromKnown;

  const sampleKeys = Object.keys(points[0]);
  return sampleKeys.filter((key) => {
    if (X_AXIS_KEYS.has(key)) return false;
    return points.some((point) => isNumericValue(point[key]));
  });
}

function normalizePoints(points, valueKeys) {
  return points.map((point) => {
    const normalized = { ...point };

    for (const key of valueKeys) {
      if (isNumericValue(normalized[key])) {
        normalized[key] = Number(normalized[key]);
      }
    }

    return normalized;
  });
}

function humanizeKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function buildLines(points, providedLines) {
  if (providedLines?.length) return providedLines;

  const detectedKeys = detectValueKeys(points);
  if (!detectedKeys.length) return [];

  return detectedKeys.map((dataKey, index) => ({
    dataKey,
    name: LINE_NAMES[dataKey] ?? humanizeKey(dataKey),
    stroke: LINE_COLORS[index % LINE_COLORS.length],
    dotColor: "#fefefe",
  }));
}

export function mapUserActivityGraph(response, defaults = {}) {
  const payload = unwrapApiPayload(response);
  const rawPoints = resolvePoints(payload);

  if (!rawPoints?.length) return null;

  const xKey = resolveXKey(rawPoints, payload);
  const lines = buildLines(rawPoints, payload?.lines ?? defaults.lines);

  if (!lines.length) return null;

  const valueKeys = lines.map((line) => line.dataKey);
  const data = normalizePoints(rawPoints, valueKeys);

  return {
    data,
    lines,
    xKey,
    title: payload?.title ?? defaults.title ?? "User Activity",
    subtitle:
      payload?.subtitle ??
      defaults.subtitle ??
      "Monthly user growth trend",
  };
}
